import React, { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import RenderLabel from "./Renderer/RenderLabel";
import RenderWidgets from "./Renderer/RenderWigets";
import { SignInFormConfig } from "../../utils/constant/formConfig.constant";
import GoogleSignInButton from "../../common/ui/GoogleSignInButton";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { GOOGLE_CLIENT_ID } from "../../utils/constant/globalEnv.constant";
import { useNavigate } from "react-router-dom";
import { googleLoginOrRegister, login } from "../../services/public.services";
import { toastMessage } from "../../utils/helper/myToast";
import { extractDeviceInfo } from "../../utils/helper/browser";
import { setSignupInfo, setValidateMfaDetails } from "../../store/features/loginSlice";
import { loginSuccessAction } from "@/store/actions/login.actions";
import { useAppDispatch } from "@/store/store";
import Button from "@/common/ui/Button";
import CustomLink from "@/common/ui/CustomLink";
import ReCaptchaInput, { ReCaptchaHandle } from "@/common/input/ReCaptchaInput";
import Secure from "@/secure/payload";
import { queryClient } from "@/main";

interface SignInFormProps {
  signupType: string;
  type: string;
}
const SignInForm: React.FC<SignInFormProps> = ({ signupType, type }) => {
  const methods = useForm({ mode: "onBlur", reValidateMode: "onChange" });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isGoogleLogin, setIsGoogleLogin] = useState(false);
  const reCaptchaRef = useRef<ReCaptchaHandle>(null);

  const {
    handleSubmit,
    // watch,
    formState: { isSubmitting },
  } = methods;
  // console.log(watch());
  // console.log({ errors });

  const handleDirectSignIn = async (formData: any) => {
    let recaptcha = null;
    if (reCaptchaRef.current) {
      recaptcha = await reCaptchaRef.current.getRecaptchaToken();
    }
    const browserInfo = await extractDeviceInfo();
    // TODO:Removed HardCode IP Address
    const res: any = await login({ browserInfo: { ...browserInfo }, recaptcha, ...formData, signupType });
    if (res?.status === 200) {
      const { message, isError, data, showMessage } = res.data;
      // clear cache if any
      queryClient.clear();
      dispatch(loginSuccessAction(data));
      navigate("/dashboard");
      localStorage.setItem("userLogin", Date.now().toString());
      return showMessage && toastMessage(message, isError);
    } else if (res?.status === 302) {
      const { data } = res.data;
      if (data?.path === "/validate-mfa") {
        handleMFA(data);
        return;
      } else if (data?.path === "/verify-account") {
        dispatch(setSignupInfo({ emailVerifyToken: Secure.payloadDecryptionValue(data?.emailVerifyToken) as string, maskIdentity: data?.maskIdentity }));
        navigate(data?.path);
        return toastMessage(res.data?.message, res?.data?.isError);
      }
    } else {
      console.log("Error", res);
      const { message } = res?.data || {};
      if (message) {
        return toastMessage(message, true);
      }
      if (res.message) {
        return toastMessage(res.message, true);
      }
    }
  };

  const handleGoogleSignIn = async (data: any) => {
    // console.log("---------->GOOGLE", { data });
    try {
      const browserInfo = await extractDeviceInfo();
      const payload: any = { browserInfo: { ...browserInfo }, signupType };
      // OneTab
      if (data?.credential) {
        console.log("One Tab Google Sign");
        payload.credentialToken = data?.credential;
      } else if (data?.code) {
        setIsGoogleLogin(true);
        console.log("Google Sign-in");
        payload.code = data?.code;
      }

      const res: any = await googleLoginOrRegister(payload);
      console.log("GOOOGLE LOGIN", { res });

      if (res?.status === 200) {
        const { message, isError, data, showMessage } = res.data;
        dispatch(loginSuccessAction(data));
        navigate("/dashboard");
        localStorage.setItem("userLogin", Date.now().toString());
        return showMessage && toastMessage(message, isError);
      } else if (res?.status === 302) {
        // console.log("This is Response", { res });
        const { data } = res?.response?.data || {};
        handleMFA(data);
      } else {
        console.log("Error", res);
        const { message, isError } = res?.response?.data || {};
        if (message) {
          return toastMessage(message, isError);
        }
        if (res.message) {
          return toastMessage(res.message, true);
        }
      }
    } catch (err) {
      console.log({ err });
    } finally {
      setIsGoogleLogin(false);
    }
  };

  const handleMFA = (data: any) => {
    dispatch(setValidateMfaDetails({ token: data.mfaToken, mfaType: data.mfaType, identity: data?.identity }));
    navigate("/validate-mfa");
  };

  return (
    <div className={`p-4 shadow-form rounded-llg`}>
      <h2 className="font-bold text-2xl text-center text-text-label">{SignInFormConfig.formName}</h2>
      <FormProvider {...methods}>
        <form className="h-fit items-center md:items-start flex flex-col w-full gap-y-3 py-6" onSubmit={handleSubmit(handleDirectSignIn)}>
          {SignInFormConfig.sections.map((section: any) => {
            return section.widgets?.map((widget: any) => {
              return (
                <div className={`flex ${widget?.className ? widget?.className : "flex-col"} w-full gap-1`} key={widget?.field}>
                  {SignInFormConfig?.showLabel && !widget?.hideLabel && (
                    <RenderLabel label={widget.label} required={widget?.validation?.required} labelInfo={widget?.labelInfo} className=" text-nowrap " />
                  )}
                  <div className="">
                    <RenderWidgets widget={widget} />
                  </div>
                </div>
              );
            });
          })}
          {/* Invisible ReCaptcha */}
          <ReCaptchaInput ref={reCaptchaRef} />
          {/* Forgot Password link*/}
          <div className=" flex w-full justify-end mb-3">
            <CustomLink className="text-13 font-normal leading-normal hover:underline" link={{ text: "Forgot password?", value: `/forgot-password/${type}` }} />
          </div>

          {/* submit Button */}
          <Button variant="primary" size="small" isFullWidth disabled={isSubmitting} isLoading={isSubmitting}>
            Login
          </Button>
        </form>
      </FormProvider>

      {/* OR */}
      <div className="flex items-center mb-4">
        <div className="flex-grow border-t border-gray-300"></div>
        <span className="flex-shrink mx-6 text-gray-600">or</span>
        <div className="flex-grow border-t border-gray-300"></div>
      </div>
      {/* Google Sign-in */}
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <GoogleSignInButton handleSignIn={handleGoogleSignIn} btnText="Login with Google" isLoading={isGoogleLogin} />
      </GoogleOAuthProvider>
    </div>
  );
};

export default SignInForm;
